import { forwardRef, useImperativeHandle, useState } from 'react';
import { AssigneeInfo, DynamicWidgetConfig, Message, VisitorInfo, WidgetConfig, WidgetState } from '../types';
import { ChatContent } from './ChatContent';
import type { PushSubscriptionPayload } from '../lib/push';

export interface ChatWidgetHandle {
  open: () => void;
  close: () => void;
}

interface ChatWidgetProps {
  config: WidgetConfig;
  state: WidgetState;
  messages: Message[];
  dynamicConfig?: DynamicWidgetConfig | null;
  assigneeInfo?: AssigneeInfo | null;
  configLoaded?: boolean;
  chatReady?: boolean;
  hidden?: boolean;
  onSendMessage: (message: string) => void;
  onSendMedia?: (file: File) => Promise<void> | void;
  onRegisterPush?: (subscription: PushSubscriptionPayload) => Promise<boolean> | boolean;
  onConnect: (visitorInfo: VisitorInfo) => void;
  onClose: () => void;
  onEndConversation: () => void;
}

// ============================================
// SVG Icons (FAB only)
// ============================================

const ChatIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
    <path
      d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z"
      fill="white"
      stroke="white"
      strokeWidth="1"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

// ============================================
// Helper: compute tooltip text
// ============================================

function getTooltipText(welcomeMsg: string | undefined): string {
  return welcomeMsg ? welcomeMsg.substring(0, 30) : 'Fale conosco';
}

// ============================================
// Main Component: FAB shell
// ============================================

export const ChatWidget = forwardRef<ChatWidgetHandle, ChatWidgetProps>(({
  config,
  state,
  messages,
  dynamicConfig,
  assigneeInfo,
  configLoaded = true,
  chatReady = true,
  hidden = false,
  onSendMessage,
  onSendMedia,
  onRegisterPush,
  onConnect,
  onClose,
  onEndConversation
}, ref) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isClosing, setIsClosing] = useState(false);

  const brandColor = dynamicConfig?.brandColor || config.brandColor || '#673AB7';
  const position = dynamicConfig?.position || config.position || 'bottom-right';
  const welcomeMsg = dynamicConfig?.welcomeMessage || config.welcomeMessage;

  // Position styles
  const positionStyles: Record<string, React.CSSProperties> = {
    'bottom-right': { position: 'fixed', bottom: 20, right: 20, zIndex: 2147483647 },
    'bottom-left': { position: 'fixed', bottom: 20, left: 20, zIndex: 2147483647 },
    'top-right': { position: 'fixed', top: 20, right: 20, zIndex: 2147483647 },
    'top-left': { position: 'fixed', top: 20, left: 20, zIndex: 2147483647 },
  };

  // Window position class
  const windowPosClass = position !== 'bottom-right' ? ` tw-window--${position}` : '';

  // Tooltip position
  const tooltipSide = position.includes('right') ? 'left' : 'right';

  // Expose open/close to parent via ref
  useImperativeHandle(ref, () => ({
    open: () => handleOpen(),
    close: () => handleClose(),
  }));

  const handleOpen = () => {
    setIsOpen(true);
    setIsClosing(false);
  };

  const handleClose = () => {
    setIsClosing(true);
    setTimeout(() => {
      setIsOpen(false);
      setIsClosing(false);
      onClose();
    }, 250);
  };

  const handleAfterEndConversation = () => {
    // Close widget UI without disconnecting socket after ending conversation
    setIsClosing(true);
    setTimeout(() => {
      setIsOpen(false);
      setIsClosing(false);
    }, 250);
  };

  // Wait for dynamic config to load before rendering (avoids position flash)
  if (!configLoaded) {
    return null;
  }

  return (
    <div id="troia-widget-container" style={positionStyles[position]}>
      {/* ============ Floating Action Button ============ */}
      {!isOpen && !hidden && (
        <button
          onClick={handleOpen}
          className="tw-fab"
          style={{ backgroundColor: brandColor }}
          aria-label="Abrir chat"
        >
          {/* Pulse ring */}
          <span className="tw-fab-pulse" style={{ borderColor: brandColor }} />

          {/* Icon */}
          <span className="tw-fab-icon" style={{ color: brandColor }}>
            <ChatIcon />
          </span>

          {/* Tooltip */}
          <span className={`tw-tooltip tw-tooltip--${tooltipSide}`}>
            {getTooltipText(welcomeMsg)}
          </span>
        </button>
      )}

      {/* ============ Chat Content (form + chat window) ============ */}
      {isOpen && (
        <ChatContent
          config={config}
          state={state}
          messages={messages}
          dynamicConfig={dynamicConfig}
          assigneeInfo={assigneeInfo}
          configLoaded={configLoaded}
          chatReady={chatReady}
          onSendMessage={onSendMessage}
          onSendMedia={onSendMedia}
          onRegisterPush={onRegisterPush}
          onConnect={onConnect}
          onEndConversation={onEndConversation}
          showCloseButton
          onClose={handleClose}
          windowPosClass={windowPosClass}
          isClosing={isClosing}
          onAfterEndConversation={handleAfterEndConversation}
        />
      )}
    </div>
  );
});

ChatWidget.displayName = 'ChatWidget';
